import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

export const Container = ({ children, className = '', maxWidth = '7xl' }) => {
  const maxWidths = {
    sm: 'max-w-screen-sm',
    md: 'max-w-screen-md',
    lg: 'max-w-screen-lg',
    xl: 'max-w-screen-xl',
    '2xl': 'max-w-screen-2xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-full',
  };

  return (
    <div className={clsx('mx-auto px-4 sm:px-6 lg:px-8', maxWidths[maxWidth], className)}>
      {children}
    </div>
  );
};

export const Section = ({ 
  children, 
  className = '',
  background = 'white',
  padding = 'normal' 
}) => {
  const backgrounds = {
    white: 'bg-white dark:bg-gray-900',
    gray: 'bg-gray-50 dark:bg-gray-800',
    gradient: 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900',
    dark: 'bg-gray-900 dark:bg-black',
  };

  const paddings = {
    none: '',
    small: 'py-12',
    normal: 'py-16 md:py-24',
    large: 'py-24 md:py-32',
  };

  return (
    <section className={clsx(backgrounds[background], paddings[padding], className)}>
      {children}
    </section>
  );
};

export const Grid = ({ 
  children, 
  cols = { sm: 1, md: 2, lg: 3 },
  gap = 6,
  className = '' 
}) => {
  const gridCols = `grid-cols-${cols.sm} md:grid-cols-${cols.md} lg:grid-cols-${cols.lg}`;
  const gridGap = `gap-${gap}`;

  return (
    <div className={clsx('grid', gridGap, className)} style={{
      gridTemplateColumns: `repeat(${cols.sm}, minmax(0, 1fr))`,
    }}>
      {children}
    </div>
  );
};

export const SectionHeader = ({ 
  title, 
  subtitle, 
  description,
  align = 'center',
  className = '' 
}) => {
  const alignments = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <div className={clsx('space-y-4 mb-12', alignments[align], className)}>
      {subtitle && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-blue-600 dark:text-blue-400 font-semibold text-sm uppercase tracking-wider"
        >
          {subtitle}
        </motion.div>
      )}
      {title && (
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white"
        >
          {title}
        </motion.h2>
      )}
      {description && (
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
        >
          {description}
        </motion.p>
      )}
    </div>
  );
};

export const Hero = ({ 
  title, 
  subtitle,
  description,
  image,
  buttons,
  overlay = true,
  className = '' 
}) => {
  return (
    <div className={clsx('relative min-h-screen flex items-center', className)}>
      {image && (
        <>
          <div className="absolute inset-0 z-0">
            <motion.img
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.5 }}
              src={image}
              alt="Hero background"
              className="w-full h-full object-cover"
            />
          </div>
          {overlay && (
            <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          )}
        </>
      )}
      
      <Container className="relative z-10">
        <div className="max-w-3xl">
          {subtitle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4"
            >
              {subtitle}
            </motion.div>
          )}
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            {title}
          </motion.h1>
          
          {description && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed"
            >
              {description}
            </motion.p>
          )}
          
          {buttons && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              {buttons}
            </motion.div>
          )}
        </div>
      </Container>
    </div>
  );
};

export const FeatureGrid = ({ features, className = '' }) => {
  return (
    <div className={clsx('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8', className)}>
      {features.map((feature, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
        >
          {feature}
        </motion.div>
      ))}
    </div>
  );
};

export const TwoColumnLayout = ({ 
  left, 
  right, 
  reverse = false,
  className = '' 
}) => {
  return (
    <div className={clsx(
      'grid grid-cols-1 lg:grid-cols-2 gap-12 items-center',
      reverse && 'lg:flex-row-reverse',
      className
    )}>
      <motion.div
        initial={{ opacity: 0, x: reverse ? 50 : -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className={reverse ? 'lg:order-2' : ''}
      >
        {left}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: reverse ? -50 : 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className={reverse ? 'lg:order-1' : ''}
      >
        {right}
      </motion.div>
    </div>
  );
};

export const Divider = ({ 
  gradient = false,
  className = '' 
}) => {
  return (
    <div className={clsx(
      'h-px w-full',
      gradient 
        ? 'bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent'
        : 'bg-gray-200 dark:bg-gray-700',
      className
    )} />
  );
};
